import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CatalogIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import PlaylistPlayIcon from '@material-ui/icons/PlaylistPlay';
import LayersIcon from '@material-ui/icons/Layers';
import MoneyIcon from '@material-ui/icons/MonetizationOn';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BuildIcon from '@material-ui/icons/Build';
import WarningIcon from '@material-ui/icons/Warning';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CatalogIcon} to="catalog" text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={MenuBookIcon} text="Operaciones">
          <SidebarSubmenu title="Operaciones">
            <SidebarSubmenuItem
              title="Operations"
              to="docs/default/component/engineering-docs/operations/"
              icon={BuildIcon}
            />
            <SidebarSubmenuItem
              title="Incidentes"
              to="docs/default/component/engineering-docs/incidentes/"
              icon={WarningIcon}
            />
          </SidebarSubmenu>
        </SidebarItem>
        <SidebarItem
          icon={CreateComponentIcon}
          to="create"
          text="Create..."
        />
        <SidebarItem
          icon={TrackChangesIcon}
          to="tech-radar"
          text="Tech Radar"
        />
        <SidebarDivider />
        <SidebarItem
          icon={AnnouncementIcon}
          to="announcements"
          text="Announcements"
        />
        <SidebarItem
          icon={PlaylistPlayIcon}
          to="playlist"
          text="Playlists"
        />
        <SidebarItem icon={LayersIcon} to="explore" text="Explore" />
        <SidebarItem icon={MoneyIcon} to="cost-insights" text="Cost Insights" />
        <SidebarDivider />
        <SidebarItem icon={CheckCircleIcon} to="entity-validation" text="Validate Entity" />
        <Shortcuts allowExternalLinks />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
